import React, { useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Typography } from '@mui/material';
import ResponsiveToolbarButton from '../../../../shared/ui/workflow/ResponsiveToolbarButton';
import ToolbarButtonGroup from '../../../../shared/ui/workflow/ToolbarButtonGroup';
import { WorkspaceStatusPill } from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { getKeyLibraryHeaderActionsSx } from './quickFillKeyLibraryStyles';

export interface QuickFillKeyLibrarySharingControlsProps {
  libraryEntryCount: number;
  editorEntryCount: number;
  hasUnsavedChanges: boolean;
  addLocalKeyDisabled: boolean;
  exportDisabled: boolean;
  onAddLocalKey: () => void;
  onExport: () => void;
  onImportFile: (file: File) => void | Promise<void>;
}

const QuickFillKeyLibrarySharingControls: React.FC<QuickFillKeyLibrarySharingControlsProps> = ({
  libraryEntryCount,
  editorEntryCount,
  hasUnsavedChanges,
  addLocalKeyDisabled,
  exportDisabled,
  onAddLocalKey,
  onExport,
  onImportFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const countLabel = hasUnsavedChanges
    ? `${libraryEntryCount} saved · ${editorEntryCount} in editor`
    : `${libraryEntryCount} saved ${libraryEntryCount === 1 ? 'entry' : 'entries'}`;
  const exportDisabledHelp = hasUnsavedChanges
    ? 'Save or revert the current edits before exporting the dictionary.'
    : 'Add or import a local key before exporting the dictionary.';

  return (
    <Stack
      role="toolbar"
      aria-label="Dictionary actions"
      direction="row"
      spacing={0.5}
      alignItems="center"
      useFlexGap
      sx={getKeyLibraryHeaderActionsSx}
    >
      <WorkspaceStatusPill
        label={countLabel}
        tone={hasUnsavedChanges ? 'warning' : libraryEntryCount ? 'success' : 'neutral'}
        size="compact"
      />
      <ToolbarButtonGroup aria-label="Dictionary file actions" density="compact">
        <ResponsiveToolbarButton
          size="small"
          icon={<FileDownloadOutlinedIcon fontSize="small" />}
          label="Export"
          aria-label="Export dictionary"
          variant="text"
          color="inherit"
          onClick={onExport}
          disabled={libraryEntryCount === 0 || exportDisabled}
          disabledHelp={exportDisabledHelp}
        />
        <ResponsiveToolbarButton
          size="small"
          icon={<FileUploadOutlinedIcon fontSize="small" />}
          label="Import"
          aria-label="Import dictionary"
          variant="text"
          color="inherit"
          onClick={() => fileInputRef.current?.click()}
        />
        <ResponsiveToolbarButton
          size="small"
          icon={<InfoOutlinedIcon fontSize="small" />}
          label="Dictionary storage and sharing"
          aria-label="About dictionary storage and sharing"
          showText={false}
          color="inherit"
          help={(
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2">
                Local keys are stored only on this workstation. They are not uploaded or saved in a
                profile.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Share exported dictionaries only internally with trusted coworkers. Imports are
                previewed and merged.
              </Typography>
            </Box>
          )}
        />
      </ToolbarButtonGroup>
      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept=".json,application/json"
        aria-label="Import dictionary file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onImportFile(file);
          event.target.value = '';
        }}
      />
      <ResponsiveToolbarButton
        size="small"
        icon={<AddIcon fontSize="small" />}
        label="Add local key"
        variant="contained"
        color="secondary"
        onClick={onAddLocalKey}
        disabled={addLocalKeyDisabled}
      />
    </Stack>
  );
};

export default QuickFillKeyLibrarySharingControls;
